import { Lightbulb, SearchCheck, UserCheck, IndianRupee, Rocket } from "lucide-react";
import { Reveal } from "./Reveal";

const CHAPTERS = [
  {
    n: "01",
    title: "Ideation Engine",
    icon: Lightbulb,
    copy: "Structured sprints that pull real problems out of your campus and city. Students learn to think like founders — not assignment writers.",
    points: ["Problem-discovery workshops", "Founder mindset bootcamps", "Campus-wide idea hunts"],
    span: "lg:col-span-7",
    testId: "manifesto-chapter-ideation",
    image: "https://images.unsplash.com/photo-1682962232755-f1d051ddb638?q=85&w=1200&auto=format&fit=crop",
    imgAlt: "Indian students collaborating around a table on startup ideas",
  },
  {
    n: "02",
    title: "Rigorous Validation",
    icon: SearchCheck,
    copy: "Every idea is stress-tested against the market before a single rupee is spent. Real customer interviews, real data, real go/no-go calls.",
    points: ["Market & customer validation", "MVP scoping", "Kill-or-scale frameworks"],
    span: "lg:col-span-5",
    testId: "manifesto-chapter-validation",
  },
  {
    n: "03",
    title: "1-on-1 Veteran Mentorship",
    icon: UserCheck,
    copy: "Each student team is paired with consultants who carry decades of scar tissue — strategy, finance, ops, brand. No gyaan, only guidance.",
    points: ["Dedicated venture mentor", "Weekly office hours", "Playbooks from 100+ yrs of consulting"],
    span: "lg:col-span-5",
    testId: "manifesto-chapter-mentorship",
    image: "https://images.unsplash.com/photo-1686624386665-4cd01b96d0f6?q=85&w=1200&auto=format&fit=crop",
    imgAlt: "Indian founders in a working session with a veteran mentor",
  },
  {
    n: "04",
    title: "Funding Pipeline",
    icon: IndianRupee,
    copy: "Direct access to the frantiger network of 100+ angels and seed funds. We prep the deck, rehearse the pitch, and sit in the room.",
    points: ["Investor network access", "Pitch deck & data room prep", "Demo day showcases"],
    span: "lg:col-span-7",
    testId: "manifesto-chapter-funding",
  },
  {
    n: "05",
    title: "GTM & Network Scaling",
    icon: Rocket,
    copy: "Go-to-market designed by operators, not textbooks. Plus the frantiger portfolio of brands as first customers, pilot partners and franchise springboards.",
    points: ["GTM strategy & launch", "Frantiger brand portfolio pilots", "Distribution & franchise models"],
    span: "lg:col-span-12",
    testId: "manifesto-chapter-gtm",
    image: "https://images.unsplash.com/photo-1716703741355-30379d2e9de6?q=85&w=1200&auto=format&fit=crop",
    imgAlt: "Indian entrepreneur presenting to a crowd",
    dark: true,
  },
];

const Manifesto = () => (
  <section id="manifesto" className="blueprint-grid bg-[#F8FAFC] py-24 lg:py-32">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <Reveal>
        <span className="font-mono-x text-xs uppercase tracking-[0.25em] text-[#0055FF]">
          / THE MANIFESTO
        </span>
        <h2 className="mt-5 font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl text-[#0A0D14] max-w-3xl leading-tight">
          Five stages. One outcome:{" "}
          <span className="font-serif-accent text-[#0055FF]">students who build.</span>
        </h2>
        <p className="mt-5 text-base text-[#64748B] max-w-2xl leading-relaxed">
          Handholding across the entire entrepreneurship journey — from the first
          scribbled idea to a funded, market-ready venture.
        </p>
      </Reveal>

      <div className="mt-14 grid grid-cols-1 md:grid-cols-12 gap-6 lg:gap-8">
        {CHAPTERS.map((c, i) => (
          <Reveal key={c.n} delay={(i % 2) * 0.1} className={`col-span-12 md:col-span-6 ${c.span}`}>
            <article
              data-testid={c.testId}
              className={`group relative h-full border transition-all duration-300 hover:-translate-y-1 ${
                c.dark
                  ? "bg-[#0A0D14] border-[#0A0D14] hover:border-[#0055FF]"
                  : "bg-white border-[#0055FF]/20 hover:border-[#0055FF]"
              }`}
            >
              <div className={c.image ? "grid grid-cols-1 sm:grid-cols-2 h-full" : "h-full flex flex-col"}>
                <div className="p-7 lg:p-9 flex flex-col">
                  <div className="flex items-start justify-between">
                    <span className={`font-display font-black text-4xl lg:text-5xl tracking-tight ${c.dark ? "text-[#0055FF]" : "text-[#0055FF]/25 group-hover:text-[#0055FF] transition-colors"}`} style={{ fontWeight: 900 }}>
                      {c.n}
                    </span>
                    <c.icon className={`w-6 h-6 ${c.dark ? "text-[#0055FF]" : "text-[#0055FF]"}`} strokeWidth={1.75} />
                  </div>
                  <h3 className={`mt-5 font-display font-semibold text-xl sm:text-2xl tracking-tight ${c.dark ? "text-white" : "text-[#0A0D14]"}`}>
                    {c.title}
                  </h3>
                  <p className={`mt-3 text-sm leading-relaxed ${c.dark ? "text-white/65" : "text-[#64748B]"}`}>
                    {c.copy}
                  </p>
                  <ul className="mt-5 space-y-2">
                    {c.points.map((p) => (
                      <li key={p} className={`flex items-center gap-2.5 font-mono-x text-[11px] uppercase tracking-[0.12em] ${c.dark ? "text-white/60" : "text-[#64748B]"}`}>
                        <span className="w-1.5 h-1.5 bg-[#0055FF] shrink-0" />
                        {p}
                      </li>
                    ))}
                  </ul>
                </div>
                {c.image && (
                  <div className="overflow-hidden min-h-[200px]">
                    <img
                      src={c.image}
                      alt={c.imgAlt}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                )}
              </div>
            </article>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export default Manifesto;
